import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Alert, Card, Filters, Page, PageHeader, Table, Toolbar, type Action } from '@evogard/ui';
import { canSeeRankingSales, loadFailureMessage, type RankingSegment } from '@evogard/domain';
import type {
  ConsultantPosition,
  Period,
  Ranking,
  RankingOrder,
} from '@evogard/contract';
import { Podium } from './Podium.js';
import { RankingSalesModal } from './RankingSalesModal.js';
import { api } from '../../lib/api.js';
import { can, useSessionContext } from '../../lib/session.js';
import {
  WhyNotSettled,
  MoneyValue,
  currentPeriod,
  periodLabel,
} from './common.js';

/**
 * O ranking de vendas da competência.
 *
 * ## Esta tela ordena desde 09/09, e o que a destravou foi uma resposta
 *
 * Duas contagens rodavam nos sistemas antigos e discordavam: uma contava venda,
 * troca e Plano Bronze; a outra contava **toda** venda que passasse pelos
 * filtros. O mesmo consultor, no mesmo mês, aparecia como 33 numa tela e 37 na
 * outra. A casa escolheu a primeira — a P5 do levantamento de remuneração —, e
 * o quadro passou a existir.
 *
 * Enquanto ninguém havia escolhido, ordenar seria a tela dizendo quem é o melhor
 * consultor da praça por palpite. Agora ela ordena, e a contagem que produziu
 * cada número é a mesma que escolhe a faixa que paga.
 *
 * ## O aviso que declarava a contagem saiu, e quem responde agora é o detalhe
 *
 * O quadro abria com um aviso no topo dizendo a regra — venda, troca e Plano
 * Bronze — porque os dois sistemas antigos contavam diferente sem avisar. Com
 * uma contagem só, ele passou a ocupar o topo em todo carregamento para repetir
 * o que nunca muda; e a pergunta que a operação faz olhando duas telas não é
 * "qual é a regra?", é "por que aqui conta 33 e a lista de vendas mostra 37?".
 *
 * Quem responde essa é **o detalhe de cada linha**, desde 10/09. Uma segunda
 * linha na célula de vendas respondia antes dele, e respondia *quantas* — a
 * metade fácil; o detalhe responde *quais*, com as placas na tela. Ver
 * `Counted`. A regra segue dita onde ela é resposta e não repetição: no
 * motivo, quando o quadro é recusado.
 *
 * ## Continua havendo o que recusa, e agora é dado e não decisão
 *
 * O motivo que sobrou é o da apuração de comissão, e some sozinho quando a
 * classificação ficar completa: venda sem classificação — a contagem escolhida
 * precisa saber o que cada placa é. O consultor sem régua declarada recusava
 * aqui também até 22/10/2026, quando a régua virou obrigatória no cadastro.
 *
 * Nesses casos a tela **não desenha lista nenhuma** — nem vazia. Uma tabela
 * vazia por falta de dado seria lida como "ninguém vendeu nesta praça", que é a
 * pior leitura possível de um ranking. A lista vazia só aparece quando ela é
 * verdade: competência apurada em que ninguém entrou na contagem.
 *
 * ## A posição vem pronta do servidor — inclusive quando a ordem muda
 *
 * Nenhum `sort` nesta tela. Ordenar no cliente é como duas telas da mesma casa
 * passam a discordar, e é a parte que o sistema antigo já fazia certo. O empate
 * de verdade — mesmo realizado e mesmo valor — divide a posição, e a seguinte
 * pula: 1º, 2º, 2º, 4º.
 *
 * O filtro de ordem **não é exceção a isso**. Ele viaja na consulta, entra na
 * chave do cache e volta na resposta; a tela nunca reordena a lista que
 * recebeu. É por isso que o pódium acompanha sem uma linha de código — ele lê
 * `position`, e a `position` já veio ordenada pelo recorte pedido.
 *
 * **E o que a tela ANUNCIA vem da resposta, nunca do estado local.** Enquanto
 * uma consulta nova não chega, `placeholderData` mantém a anterior na tela; com
 * a frase escrita a partir do `<select>`, a tela afirmaria por dois segundos um
 * recorte que não foi o que ordenou o que está abaixo dela — um quadro coroando
 * a pessoa errada sob a legenda certa.
 *
 * ## A ordem é recorte, e a contagem é regra da casa
 *
 * As duas não se misturam. A contagem — venda, troca e Plano Bronze — é a P5, é
 * o número que escolhe a faixa que paga, e não muda por filtro nenhum:
 * `achieved`, `ignored` e o valor de cada linha são idênticos nas duas ordens. O
 * que a ordem muda é quem aparece na frente. Por mudar exatamente isso, ela é
 * dita ao lado da lista.
 *
 * ## A linha abre as vendas que produziram o número dela
 *
 * `Counted` responde *quanto* a contagem descartou; a coluna "Vendas da
 * posição" responde **quais**, e as duas metades juntas é que tornam o número
 * conferível contra a mesa de vendas. Ela tem permissão própria
 * (`sales.ranking:ver_vendas`) porque o que ela mostra é outra coisa do que o
 * quadro mostra — placas, com código do veículo, do associado e valor, uma a
 * uma. Quem só tem `ver` continua com o quadro inteiro e sem a coluna: o botão
 * some, e o guard nega a rota se alguém a chamar assim mesmo.
 *
 * ## O CARTÃO DOS TIMES saiu em 18/09, e não foi substituído aqui
 *
 * Esta tela tinha um segundo cartão somando o realizado por equipe, sob
 * `sales.ranking:tabela_times_ver`. Ele respondia *qual time está na frente* e
 * não respondia nada do que um líder pergunta: quem produziu, o quê, e quais
 * placas. Para ir do time à pessoa, a operação rolava de volta para a tabela de
 * cima e procurava de olho.
 *
 * A pergunta virou uma tela — `Comercial › Painel do time` —, e o cartão saiu
 * junto com a ação que o mostrava. Somar o mesmo time em dois lugares é como
 * §5.6 começou.
 *
 * **O filtro por time FICOU**, e ele não soma nada: ele estreita esta lista de
 * gente a uma equipe e recalcula a posição dentro dela. As duas coisas eram
 * ações separadas desde sempre, e é por isso que tirar uma não levou a outra.
 *
 * A hierarquia não muda o valor de ninguém: não há rateio de comissão entre
 * consultor, líder e time em lugar nenhum desta casa.
 */
export function RankingScreen() {
  const { data: context } = useSessionContext();
  const [period, setPeriod] = useState<Period>(currentPeriod());
  /*
   * `quantidade` é a ordem que este quadro sempre teve — a do
   * `ranking-comercial` (§5.6). Ela é o estado inicial da TELA, e não um padrão
   * do contrato: quem consulta a API declara o recorte, sempre.
   */
  const [order, setOrder] = useState<RankingOrder>('quantidade');
  /*
   * O time do filtro — `''` é "todos", e a rota recebe o campo AUSENTE.
   *
   * Ele só é MANDADO por quem tem `sales.ranking:filtro_times`: a rota recusa
   * com 403 quem manda sem a ação, e um estado que sobrevivesse à perda da
   * permissão faria a tela pedir o que ela não pode. Como o seletor só existe
   * com a ação, o estado só sai do vazio com ela.
   */
  const [teamId, setTeamId] = useState('');
  /*
   * O quadro — interno ou externo (`comercial-41`). `''` é "o que o servidor
   * escolher": o do perfil de quem olha, ou o externo. Só é MANDADO por quem
   * recebeu `segmentOptions`, pelo mesmo motivo do time: pedir o quadro do
   * outro segmento sem poder escolher é recusado.
   */
  const [segment, setSegment] = useState<RankingSegment | ''>('');
  const canFilterTeams = can(context, 'sales.ranking', 'filtro_times');
  const tenantId = context?.currentTenant.id;

  const rankingQuery = useQuery({
    queryKey: ['comercial', 'ranking', tenantId, period, order, teamId, segment],
    queryFn: async () => {
      const r = await api.sales.ranking({
        query: {
          period,
          order,
          teamId: teamId ? Number(teamId) : undefined,
          segment: segment || undefined,
        },
      });
      if (r.status !== 200) {
        throw new Error(loadFailureMessage(r.status, 'o ranking desta competência'));
      }
      return r.body;
    },
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[2] === tenantId ? previous : undefined,
  });

  const ranking = rankingQuery.data;

  return (
    <Page measure="cheia">
      <PageHeader title="Ranking de vendas" />

      {/*
       * A barra de `@evogard/ui`, e não uma `Table` com `toolbar`: o recorte
       * governa o pódium, o quadro de consultores e o de times. Prendê-lo a uma
       * das tabelas faria o filtro de uma mudar a outra — a barra mentindo
       * sobre o próprio alcance.
       *
       * Sem campo de busca: não há recorte de texto nesta tela, e um campo de
       * pesquisa que não pesquisa nada é a barra prometendo o que a rota não
       * faz.
       *
       * **Nem a competência nem a ordem têm vazio**, e o "Limpar" do painel
       * aplica o vazio. Os dois caem para o estado em que a tela nasce — o mês
       * de hoje e a contagem de vendas —, e cada campo diz isso no `help`: o
       * vazio aqui é uma escolha declarada, e não um recorte recusado em
       * silêncio.
       */}
      <Toolbar
        toolbar={
          <Filters
            values={{
              period,
              /*
               * `quantidade` viaja como VAZIO, e não como o próprio valor.
               *
               * `selecao` sempre desenha a opção vazia, e as duas ordens são a
               * lista inteira — com `quantidade` também entre as opções, o campo
               * teria "Vendas" duas vezes, uma como padrão e outra como escolha,
               * e nada na tela diria que são a mesma coisa. Vazio É a ordem que
               * a tela sempre teve (a do `ranking-comercial`, §5.6), e por isso
               * ele também não conta no número do gatilho: ordem padrão não é
               * recorte que alguém declarou.
               */
              order: order === 'faturamento' ? 'faturamento' : '',
              teamId,
              segment,
            }}
            onApply={(values) => {
              const chosenPeriod = String(values.period ?? '');
              setPeriod((chosenPeriod || currentPeriod()) as Period);
              setOrder(values.order === 'faturamento' ? 'faturamento' : 'quantidade');
              /*
               * O time só é MANDADO por quem tem `sales.ranking:filtro_times`:
               * a rota recusa com 403 quem manda sem a ação. Como o campo só
               * existe com ela, o estado só sai do vazio com ela — e aqui a
               * guarda repete isso, porque o painel devolve todas as chaves
               * declaradas e não só as que foram mexidas.
               */
              setTeamId(canFilterTeams ? String(values.teamId ?? '') : '');
              const chosenSegment = String(values.segment ?? '');
              setSegment(
                ranking?.estado === 'apurado' &&
                  ranking.segmentOptions !== null &&
                  (chosenSegment === 'interno' || chosenSegment === 'externo')
                  ? chosenSegment
                  : '',
              );
            }}
            fields={[
              {
                type: 'periodo',
                name: 'period',
                label: 'Competência',
                help: 'Vazio volta ao mês de hoje.',
              },
              {
                type: 'selecao',
                name: 'order',
                label: 'Ordenar por',
                placeholder: ORDER_LABEL.quantidade,
                help: 'A coluna que decide a frente. Ela não muda a contagem de ninguém.',
                options: [
                  { value: 'faturamento', label: ORDER_LABEL.faturamento },
                ],
              },
              /*
               * O filtro de time, e ele some inteiro sem
               * `sales.ranking:filtro_times`.
               *
               * As opções vêm de `teamOptions`, que o servidor só monta com a
               * mesma ação — e são TODOS os times da base, não só os que
               * pontuaram: "este time vendeu em março?" é a pergunta, e esconder
               * a opção do time que não vendeu deixaria a pessoa sem como
               * fazê-la.
               *
               * O `help` avisa o que o filtro faz com o número da posição,
               * porque ele muda de significado: filtrado, o primeiro do time é
               * 1º. Sem a frase, a mesma tela mostraria "1º" para duas coisas
               * diferentes e nada diria qual delas está na frente.
               */
              /*
               * O quadro, e ele só existe para quem o servidor deixa escolher
               * (`segmentOptions`). O consultor interno vê o do interno e o
               * externo o do externo, sem campo nenhum.
               */
              ranking?.estado === 'apurado' &&
                ranking.segmentOptions !== null && {
                  type: 'selecao' as const,
                  name: 'segment',
                  label: 'Quadro',
                  placeholder: 'O do seu perfil',
                  help: 'Sem perfil de consultor, o padrão é o quadro do externo. A posição é contada dentro do quadro.',
                  options: ranking.segmentOptions.map((value) => ({
                    value,
                    label: SEGMENT_LABEL[value],
                  })),
                },
              canFilterTeams &&
                ranking?.estado === 'apurado' &&
                ranking.teamOptions !== null &&
                ranking.teamOptions !== undefined && {
                  type: 'selecao' as const,
                  name: 'teamId',
                  label: 'Time',
                  placeholder: 'Todos os times',
                  help: 'A posição é recalculada dentro do time escolhido.',
                  options: ranking.teamOptions.map((t) => ({
                    value: String(t.id),
                    label: t.nome,
                  })),
                },
            ]}
          />
        }
      />

      {rankingQuery.isError && <Alert tone="erro">{rankingQuery.error.message}</Alert>}

      {rankingQuery.isPending && !rankingQuery.isError && (
        <Card title="Carregando…">
          <span className="esqueleto block h-4 w-2/3" />
          <span className="esqueleto mt-2 block h-4 w-1/2" />
        </Card>
      )}

      {ranking?.estado === 'nao_apuravel' && (
        <WhyNotSettled
          title={`O ranking de ${periodLabel(period)} não é apurável.`}
          reasons={ranking.reasons}
        >
          <p className="mt-1">
            Nenhuma lista é mostrada abaixo, e isso é deliberado: uma tabela vazia diria
            que ninguém vendeu nesta praça, e uma tabela parcial diria quem está na frente
            deixando gente de fora sem avisar. As duas seriam afirmações que a casa não
            pode fazer. O critério da contagem já está decidido — venda, troca e Plano
            Bronze —; o que falta acima é dado de cadastro, e ele se resolve nas telas que
            cada motivo aponta.
          </p>
        </WhyNotSettled>
      )}

      {ranking?.estado === 'apurado' && <RankingMeasured ranking={ranking} />}
    </Page>
  );
}

/**
 * O nome de cada ordem na tela, escrito **uma vez**.
 *
 * O `satisfies Record<RankingOrder, string>` faz um valor novo do contrato
 * quebrar a compilação em vez de aparecer como `faturamento` cru no `<select>`
 * e "Faturamento" na legenda da lista — que é como a mesma tela passaria a
 * dizer duas coisas sobre o recorte que ela mesma pediu.
 *
 * O campo do painel não varre `RANKING_ORDERS`, e é decisão: a ordem padrão
 * viaja como vazio, então a lista de opções é a das ordens MENOS a padrão. Um
 * terceiro valor no contrato continua quebrando aqui — é para isso que o
 * `satisfies` está logo abaixo.
 *
 * O rótulo nomeia a COLUNA da tabela, e é de propósito: quem escolhe "Vendas"
 * consegue apontar para a coluna que ordenou o quadro. "Desempenho" ou
 * "Ranking" seriam palavras que não estão em lugar nenhum da tela.
 */
/** O nome de cada quadro — um por segmento do contrato. */
// @valor
const SEGMENT_LABEL = {
  interno: 'consultor interno',
  externo: 'consultor externo',
} as const satisfies Record<RankingSegment, string>;

// @valor
const ORDER_LABEL = {
  quantidade: 'Vendas',
  faturamento: 'Faturamento',
} as const satisfies Record<RankingOrder, string>;

/**
 * O ranking apurado.
 *
 * `ignored` aparece ao lado de `achieved` na mesma célula, e não numa coluna
 * própria: é o que permite conferir a diferença entre as 37 linhas que a
 * operação vê na tela de vendas e as 33 que este quadro conta. Sem ele o número
 * fica certo e inconferível, que é como os dois quadros antigos conviviam.
 */
function RankingMeasured({
  ranking,
}: {
  ranking: Extract<Ranking, { estado: 'apurado' }>;
}) {
  const { data: context } = useSessionContext();
  /*
   * As duas ações, e a decisão por linha é de `canSeeRankingSales` — a MESMA
   * função que o serviço chama antes de devolver 200 ou 403.
   *
   * Não é zelo: a régua tem uma metade que o cliente não tem como adivinhar (de
   * que time é quem olha, que vem em `ranking.viewerTeam`) e uma armadilha que
   * uma reimplementação distraída cai calada — `null === null` faria dois
   * consultores sem time parecerem do mesmo time. Escrever a comparação aqui
   * seria a segunda cópia de uma regra de acesso.
   *
   * Esconder continua não protegendo: quem protege é o serviço. Isto decide se
   * o botão existe, e um botão que abre um 403 é a tela afirmando o que o
   * código não cumpre.
   */
  const reach = {
    any: can(context, 'sales.ranking', 'ver_vendas'),
    team: can(context, 'sales.ranking', 'ver-venda-time'),
  };
  const canSeeSales = reach.any || reach.team;
  /*
   * A posição inteira, e não o id: o modal escreve o nome no título e mostra o
   * total da linha para ser conferido contra a lista. Guardar só o id faria a
   * tela buscar de novo o que já tem na mão — e abriria a chance de o título
   * dizer um nome e a lista ser de outra pessoa enquanto a consulta não chega.
   */
  const [detailing, setDetailing] = useState<ConsultantPosition | null>(null);

  return (
    <div className="flex flex-col gap-5">
      <Card
        title={`Consultores em ${periodLabel(ranking.period)}`}
        bleed
      >
        {/*
         * O pódium vem ANTES da tabela e dentro do mesmo cartão, porque ele não
         * é outro assunto: é o topo desta lista, desenhado. Num cartão próprio
         * ele viraria um segundo quadro de consultores, e a operação teria duas
         * peças para conferir uma contra a outra — que é o defeito que este
         * módulo herdou dos dois rankings antigos.
         *
         * Ele desenha só quando pode afirmar 1º, 2º e 3º distintos; caso
         * contrário devolve nada e a tabela abaixo responde sozinha. Ver o
         * docblock de `Podium`.
         */}
        <Podium consultants={ranking.consultants} period={ranking.period} />

        <Table
          label="Consultores no ranking da competência"
          items={ranking.consultants}
          rowKey={(p) => String(p.consultant.id)}
          empty="A competência foi apurada e nenhum consultor entrou na contagem. Isto é uma resposta sobre o mês, não uma falha."
          rowTitle={(p) => `${p.position}º · ${p.consultant.user.nome}`}
          rowIcon="comercial"
          /*
           * A ação da linha no painel das reticências, e não mais um botão de
           * olho numa coluna "Vendas da posição".
           *
           * **Devolver lista vazia é a resposta certa**, e é o que a coluna
           * fazia com a célula vazia: sem nenhuma das duas ações a linha não tem
           * gatilho nenhum, e com a ação de time as linhas de fora do time
           * também não. O quadro continua com a mesma forma, e o que abre é o
           * que ela alcança. Esconder não protege — quem protege é o serviço.
           *
           * A decisão por linha é de `canSeeRankingSales`, a MESMA função que o
           * serviço chama antes de devolver 200 ou 403: a régua tem uma metade
           * que o cliente não adivinha (de que time é quem olha) e uma armadilha
           * que uma reimplementação distraída cai calada — `null === null` faria
           * dois consultores sem time parecerem do mesmo time.
           */
          rowActions={(p): Action[] =>
            canSeeSales &&
            canSeeRankingSales(
              reach,
              ranking.viewerTeam?.id ?? null,
              p.team?.id ?? null,
            )
              ? [
                  {
                    label: `Ver as ${p.achieved + p.ignored} vendas desta posição`,
                    icon: 'olho',
                    run: () => setDetailing(p),
                  },
                ]
              : []
          }
          columns={[
            {
              title: 'Posição',
              cell: (p) => <span className="tabular-nums">{p.position}º</span>,
            },
            {
              title: 'Consultor',
              alignment: 'inicio' as const,
              minWidth: '14rem',
              cell: (p) => <p className="min-w-0 truncate">{p.consultant.user.nome}</p>,
            },
            {
              title: 'Vendas',
              alignment: 'fim',
              cell: (p) => <Counted achieved={p.achieved} />,
            },
            {
              title: 'Valor',
              alignment: 'fim',
              cell: (p) => <MoneyValue value={p.value} />,
            },
          ]}
        />
      </Card>

      {detailing && (
        <RankingSalesModal
          consultant={detailing.consultant}
          period={ranking.period}
          source="ranking"
          open
          onClose={() => setDetailing(null)}
        />
      )}

    </div>
  );
}

/**
 * O realizado da linha — o número, e só ele.
 *
 * ## O que ficava aqui até 10/09, e por que saiu
 *
 * Uma segunda linha dizia o descarte: *"4 reativações fora"*. Ela existia para
 * responder a pergunta que a operação faz olhando duas telas — *a lista de
 * vendas mostra 37 e o ranking conta 33, quem está errado?* — e respondia
 * **quantas**, que é a metade fácil. A metade que resolve é *quais*, e para
 * essa ela não tinha resposta: não havia onde olhar.
 *
 * Agora há. A linha abre o detalhe, e lá as placas descartadas aparecem uma a
 * uma, marcadas, ao lado das que contaram. Com o detalhe existindo, a segunda
 * linha virava o mesmo número dito em três lugares do mesmo cartão — pódium,
 * célula e modal —, e nenhum dos dois primeiros conferível ali.
 *
 * `ignored` continua vindo do servidor e continua sendo usado nesta tela: é
 * ele, somado ao realizado, que diz quantas placas o botão do detalhe vai
 * abrir.
 */
function Counted({ achieved }: { achieved: number }) {
  return <span className="tabular-nums">{achieved}</span>;
}
